import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { LiveLogOptions } from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";
import { maskedDownloadText, useLiveLogContainers } from "@/api/livelogs";
import { refPath, withSearch } from "@/components/drill";
import { num } from "@/lib/format";
import { Panel } from "@/components/primitives";
import { LIVE_LOG_WINDOW, useLiveLogs } from "./useLiveLogs";
import { Notice, SessionPanel } from "./sessionPanel";
import { LiveLogControls, LiveLogViewport } from "./LiveLogsView";
import { parseLiveLogAnsi } from "./liveLogAnsi";
import { useT } from "@/i18n/I18nProvider";

/**
 * Live Logs 탭 — ADR 0021 · Issue #9
 * --------------------------------------------------------------------------
 * Quickwit Logs Explorer를 대체하지 않습니다. 수집 지연·파이프라인 장애에서
 * kubelet의 지금을 보는 짧은 진단 창이고, 장기 검색은 링크로 넘깁니다.
 *
 * 두 방향 이동 모두 cluster·namespace·pod 이름·UID·container·시간 범위를 보존합니다.
 */
export function LiveLogs({
  clusterId,
  namespace,
  podName,
  podUid,
  search,
  ownerIdentity = "",
  autoOpen = false,
  initialContainer = "",
}: {
  clusterId: string;
  namespace: string;
  podName: string;
  podUid: string;
  search: string;
  /** 같은 Pod UID라도 Workload/Scope 소유 경계가 바뀌면 이전 세션을 버립니다. */
  ownerIdentity?: string;
  /** Logs Explorer에서 `live=1`로 넘어온 경우 바로 엽니다(연결은 사용자가 시작). */
  autoOpen?: boolean;
  /** Logs Explorer가 보존해 준 container 선택. */
  initialContainer?: string;
}) {
  const [open, setOpen] = useState(autoOpen);
  const catalog = useLiveLogContainers(clusterId, namespace, podName, podUid, open);
  const session = useLiveLogs(clusterId, podName, namespace, podUid, ownerIdentity);
  const [options, setOptions] = useState<LiveLogOptions>({
    container: initialContainer,
    tailLines: 500,
    timestamps: true,
  });
  const [copyMessage, setCopyMessage] = useState("");
  const { t } = useT("common");

  /* 같은 컴포넌트 인스턴스를 유지한 채 Pod 신원만 바꿉니다. 훅이 이전 stream의
     abort settlement를 기다리는 동안 새 대상의 옵션·표시 상태는 즉시 초기화됩니다. */
  useEffect(() => {
    setOptions({ container: initialContainer, tailLines: 500, timestamps: true });
    setCopyMessage("");
    setOpen(Boolean(autoOpen && podUid));
  }, [clusterId, namespace, podName, podUid, ownerIdentity, initialContainer, autoOpen]);

  const containers = catalog.data?.containers ?? [];
  const selected = options.container || containers[0]?.name || "";
  const container = containers.find((c) => c.name === selected);
  const limits = catalog.data?.limits;

  const quickwitHref = useMemo(
    () =>
      withSearch("/logs", search, {
        /* 전역 선택과 구분되는 상세 Pod의 경로 신원입니다. */
        podNamespace: namespace,
        pod: podName,
        uid: podUid,
        ...(selected ? { container: selected } : {}),
      }),
    [search, namespace, podName, podUid, selected],
  );

  const download = () => {
    const normalized = session.lines.map((line) => ({
      ...line,
      maskedLine: parseLiveLogAnsi(line.maskedLine, 0).normalizedPlain,
    }));
    const text = maskedDownloadText(normalized);
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${podName}-${selected || "container"}-masked.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyLine = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage(t("common.live.copy.success"));
    } catch {
      setCopyMessage(t("common.live.copy.failed"));
    }
  };

  /**
   * 재접속이 이어보기 지점을 실제로 씁니다.
   *
   * 서버가 준 `resume.sinceTime`은 **같은 Pod UID·container·옵션**일 때만 적용합니다.
   * 대상이 바뀌었는데 이전 지점을 들고 가면 다른 인스턴스의 시각으로 조회하게 됩니다.
   * kubelet 경계가 포함이라 마지막 줄 하나가 겹칠 수 있고, 화면이 그 사실을 밝힙니다.
   */
  const resumeFor = (merged: LiveLogOptions): string | undefined => {
    const hint = session.control?.resume?.sinceTime;
    const prev = session.active;
    if (!hint || !prev) return undefined;
    const sameTarget =
      prev.container === merged.container &&
      Boolean(prev.previous) === Boolean(merged.previous) &&
      Boolean(prev.follow) === Boolean(merged.follow) &&
      Boolean(prev.timestamps) === Boolean(merged.timestamps);
    return sameTarget ? hint : undefined;
  };

  const begin = (next: Partial<LiveLogOptions>) => {
    const merged = { ...options, ...next, container: next.container ?? selected };
    const sinceTime = resumeFor(merged);
    setOptions(merged);
    /* sinceTime과 sinceSeconds는 함께 보낼 수 없습니다 — 서버가 400으로 거절합니다. */
    session.start(sinceTime ? { ...merged, sinceTime, sinceSeconds: undefined } : merged);
  };

  const catalogError = catalog.error instanceof HttpError ? catalog.error : undefined;
  /* HttpError면 서버 문구를, 아니면(프레임 파손 등 클라이언트 오류) 그 메시지를 씁니다.
     예전에는 HttpError일 때만 렌더해 클라이언트 오류가 조용히 사라졌습니다. */
  const streamMessage =
    session.error instanceof HttpError ? session.error.body.message : session.error?.message;

  if (!podUid || !podName || !namespace) {
    return (
      <Panel title="Live Logs" subtitle={t("common.live.target.prompt")} flush>
        <p className="muted" style={{ padding: "var(--space-5)" }}>{t("common.live.target.singleStream")}</p>
      </Panel>
    );
  }

  return (
    <SessionPanel
      title="Live Logs"
      subtitle="kubelet의 현재 stdout/stderr · 서버에서 마스킹 후 전송 · 장기 검색은 Logs Explorer"
      actions={
        <div className="row row--wrap">
          <Link to={quickwitHref}>Quickwit에서 더 찾기 →</Link>
        </div>
      }
      open={open}
      onOpen={() => setOpen(true)}
      openLabel="Live Logs 열기"
      openHint="여는 순간에만 서버가 kubelet 연결을 만듭니다. 연결은 세션 상한에서 자동으로 끊깁니다."
      loading={catalog.isLoading}
      loadingLabel="container 목록을 불러오는 중…"
      error={
        catalogError ? (
          <>
            {catalogError.body.message}
            {catalogError.status === 409 && (
              <>
                {" "}
                <Link to={refPath({ podName, namespace }, search)}>현재 Pod 다시 열기 →</Link>
              </>
            )}
          </>
        ) : undefined
      }
    >
      <>
          <LiveLogControls
            containers={containers}
            selected={selected}
            options={options}
            limits={limits}
            status={session.status}
            terminating={catalog.data?.terminating ?? false}
            previousAvailable={container?.previousAvailable ?? false}
            onChange={setOptions}
            onStart={begin}
            onStop={session.stop}
            onClear={session.clear}
            onDownload={download}
            downloadable={session.lines.length > 0}
          />
          {streamMessage && (
            <Notice tone="warning" role="alert">
              {streamMessage}
            </Notice>
          )}
          {copyMessage && <Notice role="status">{copyMessage}</Notice>}
          {session.control && (
            <Notice role="status">
              {session.control.message}
              {session.control.resume && (
                <>
                  {" "}
                  내려받은 줄 <span className="num">{num(session.control.resume.deliveredLines)}</span>
                  {session.control.resume.sinceTime
                    ? " · 같은 조건으로 다시 연결하면 끊긴 지점부터 이어집니다(경계가 포함이라 마지막 줄 하나가 겹칠 수 있습니다)."
                    : " · 다시 연결하면 최근 구간부터 새로 받습니다."}
                </>
              )}
            </Notice>
          )}
          <LiveLogViewport
            lines={session.lines}
            dropped={session.dropped}
            window={LIVE_LOG_WINDOW}
            status={session.status}
            sessionKey={`${ownerIdentity}\u0000${clusterId}\u0000${namespace}\u0000${podUid}\u0000${session.sessionId}`}
            onCopyLine={(text) => void copyLine(text)}
          />
      </>
    </SessionPanel>
  );
}
